<template>
  <div :class="`${isSubmitted ? 'px-2 mt-2' : 'custom-container'}`">
      <b-img
        v-if="!isSubmitted"
        :src="bgImg"
        class="img-bg"
      />
    <div v-show="!isSubmitted" class="auth-inner py-2 with-opacity">
      <!-- Loading -->
      <loading
        v-if="isLoading || isSubmitting"
        :active="true" 
        :is-full-page="true"
        color="#498ceb"
      />

      <b-card v-if="!isLoading" class="mb-0 card-width" style="background-color: rgba(255, 255, 255, 0.7);">
        <!-- Logo -->
        <b-link class="brand-logo d-flex justify-content-center mb-2 align-items-center">
          <add-co-logo />
          <h2 :class="`text-primary font-weight-bolder custom-h2`">
            AddCo
          </h2>
          <b-img v-if="isVielcoClient" :src="logoVielco" style="width: 8rem; height: auto;"/>
        </b-link>

        <!-- Not found message -->
        <b-card v-if="clientNotFound">
          <h2 class="text-danger">
            {{ $t('client_not_found_check_url') }}
          </h2>
        </b-card>

        <div v-else>
          <b-row>
            <!-- New complaint -->
            <b-col cols="12" md="6" class="px-3 responsive-border">
              <b-card-title class="mb-1">
                {{ $t('New Complaint') }}
              </b-card-title>

              <b-card-text class="mb-2">
                {{ $t('ifWishNewComplaint') }}:
              </b-card-text>
              
              <b-button variant="warning" block @click="goToNewComplaint">
                {{ $t('Report') }}
              </b-button>

              <!-- Separador horizontal sólo en pantallas chicas -->
              <hr class="d-md-none my-2">
            </b-col>

            <!-- Complaint follow up -->
            <b-col cols="12" md="6" class="px-3">
              <!-- Title -->
              <b-card-title class="mb-1">
                {{ $t('complaintsFollowUp') }}
              </b-card-title>
              <b-card-text class="mb-2">
                {{ $t('enterUserAndCaseNumbers') }}:
              </b-card-text>
      
              <!-- Form -->
              <validation-observer
                ref="followUpForm"
                #default="{invalid}"
              >
                <b-form
                  class="auth-login-form mt-2"
                  @submit.prevent="submitData"
                >
      
                  <!-- User number -->
                  <b-form-group :label="$t('message.user_number')" label-for="userNumber">
                    <validation-provider :name="$t('message.user_number')" rules="required" #default="{ errors }">
                      <b-form-input
                        v-model="userNumber"
                        :state="errors.length > 0 ? false : null"
                        :placeholder="$t('message.user_number')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
      
                  <!-- Case number -->
                  <b-form-group :label="$t('message.case_number')" label-for="caseNumber">
                    <validation-provider :name="$t('message.case_number')" rules="required" #default="{ errors }">
                      <b-form-input
                        v-model="caseNumber"
                        :state="errors.length > 0 ? false : null"
                        :placeholder="$t('message.case_number')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
        
                  <!-- Submit -->
                  <b-button variant="primary" type="submit" class="mt-2" block :disabled="invalid">
                    {{ $t('message.submit') }}
                  </b-button>
                </b-form>
              </validation-observer>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>

    <!-- Complaint form -->
    <div v-show="isSubmitted">
      <!-- Go back button -->
      <b-card>
        <b-button variant="secondary" @click="isSubmitted = false">
          <span class="mr-1">&#11148;</span>{{ $t('Back') }}
        </b-button>
      </b-card>

      <!-- Form -->
      <Form
        :data="complaintData"
        :isSubmitting="false"
        :updateActiveTab="updateActiveTab"
        :readOnly="true"
        :userType="userType"
        :clientId="clientData._id"
        @addNewMessage="addNewMessage"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, set } from "@vue/composition-api"
import { ValidationObserver, ValidationProvider, localize } from 'vee-validate'
import { BButton, BForm, BFormInput, BFormGroup, BCard, BCardTitle, BCardText, BLink, BImg } from 'bootstrap-vue'
import { required, email } from '@validations'
import { useRouter } from '@core/utils/utils'
import AddCoLogo from '@core/layouts/components/Logo.vue'
import useJwt from '@/auth/jwt/useJwt'
import i18n from '@/libs/i18n'
import useNotifications from "@/composables/useNotifications"
import Form from '@/views/organization/complaint/shared/Form.vue'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { useLambda } from '@/views/habit/awsLambda'
import { flatPickrConfig } from '@/constants'
import bannerVielco from '@/assets/images/banner/complaint-vielco.jpg'
import bannerDefault from '@/assets/images/banner/complaint-default.png'
import logoVielco from '@/assets/images/logo/client/logo-vielco.png'

export default {
  components: {
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BCardTitle,
    BCardText,
    BLink,
    AddCoLogo,
    ValidationProvider,
    ValidationObserver,
    Form,
    Loading,
    BImg
  },
  setup() {
    const { showErrorMessage } = useNotifications();
    const { route, router } = useRouter()
    const { updateItem, getItemsAggregate, getItem } = useLambda()
    const { clientPublicId } = route.value.params
    const userNumber = ref('')
    const caseNumber = ref('')
    const clientData = ref({})
    const clientNotFound = ref(false)
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const isSubmitted = ref(false)
    const complaintData = ref({})
    const updateActiveTab = ref(false)
    const collection = 'complaint'
    const userType = ref('')
    const startOfToday = new Date()
    const endOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)
    endOfToday.setHours(23, 59, 59, 999)
    const bgImg = computed(() => clientPublicId === '90217052513' ? bannerVielco : bannerDefault)
    const isVielcoClient = computed(() => clientPublicId === '90217052513' ? true : false)


    const setState = (c, startOfToday) => {
      switch (c.stage) {
        case "end":
          return "completed";

        case "registration":
          return (c.dueDate && new Date(c.dueDate) < startOfToday) ? "delayed" : "upToDate";

        case "analysis":
          return (c.proposalDueDate && new Date(c.proposalDueDate) < startOfToday) ? "delayed" : "upToDate";

        case "approval":
          return (c.approvalDueDate && new Date(c.approvalDueDate) < startOfToday) ? "delayed" : "upToDate";

        case "corrective": {
          const anyDelayed = c.actionPlans?.some(plan =>
            plan.endDate ? new Date(plan.endDate) <= startOfToday : false
          );
          return anyDelayed ? "delayed" : "upToDate";
        }

        case "verification":
          return (c.verificationDueDate && new Date(c.verificationDueDate) < startOfToday) ? "delayed" : "upToDate";

        default:
          return "upToDate";
      }
    }

    const submitData = async () => {
      isSubmitting.value = true

      try {
        const query = {
          caseNumber: caseNumber.value,
          $or: [
            { authorUserNumber: userNumber.value },
            { affectedUserNumber: userNumber.value },
            { reportedUserNumber: userNumber.value },
          ]
        }

        const pipeline = [
          { $match: query },
          {
            $lookup: {
              from: 'worker',
              localField: 'proposalResponsible',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'proposalResponsible'
            }
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'approvalResponsible',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'approvalResponsible'
            }
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'verificationResponsible',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'verificationResponsible'
            }
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'reportedPerson',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'reportedPerson'
            }
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'reportedPersonResponsible',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'reportedPersonResponsible'
            }
          },
          {
            $lookup: {
              from: 'location',
              localField: 'areas',
              foreignField: '_id',
              pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'areas'
            }
          },
          {
            $lookup: {
              from: 'improvement',
              localField: 'associatedCases',
              foreignField: '_id',
              pipeline: [ { $project: { note: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
              as: 'associatedCases'
            }
          },
          {
            $lookup: {
              from: 'action_plan',
              localField: 'actionPlans',
              foreignField: '_id',
              pipeline: [
                {
                  $lookup: {
                    from: 'worker',
                    localField: 'executionResponsible',
                    foreignField: '_id',
                    pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                    as: 'executionResponsible'
                  }
                },
                {
                  $lookup: {
                    from: 'worker',
                    localField: 'lastModifiedBy',
                    foreignField: '_id',
                    pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ],
                    as: 'lastModifiedBy'
                  }
                },
                { $addFields: { _id: { $toString: "$_id" }, executionResponsible: { $arrayElemAt: ["$executionResponsible", 0] }, lastModifiedBy: { $arrayElemAt: ["$lastModifiedBy", 0] } } },
              ],
              as: 'actionPlans'
            }
          },
          { $addFields: { _id: { $toString: "$_id" }, assignee: { $arrayElemAt: ["$assignee", 0] }, proposalResponsible: { $arrayElemAt: ["$proposalResponsible", 0] }, approvalResponsible: { $arrayElemAt: ["$approvalResponsible", 0] }, verificationResponsible: { $arrayElemAt: ["$verificationResponsible", 0] }, reportedPersonResponsible: { $arrayElemAt: ["$reportedPersonResponsible", 0] } } },
        ]
      
        const items = await getItemsAggregate({ collection, pipeline })
        if (!items?.[0]) throw new Error("invalidUserAndCaseNumbers")

        const c = items[0]

        // Sort action plans by origin index
        c.actionPlans?.sort((a, b) => a.originIndex - b.originIndex)

        complaintData.value = {
          ...c,
          themesComputed: computed(() => c.themes?.map(theme => theme.isNewOption ? theme.value : i18n.t(`complaintThemes.${theme.value}`)).join(" / ")),
          companyRelationshipComputed: c.companyRelationship ? computed(() => c.companyRelationship.isNewOption ? c.companyRelationship.value : i18n.t(`companyRelationships.${c.companyRelationship.value}`)) : '',
          reportedPerson: c.reportedPerson?.map(({ name }) => getName(name)),
          reportedPersonResponsible: getName(c.reportedPersonResponsible?.name),
          areas: c.areas?.map(({ location }) => getName(location)),
          associatedCases: c.associatedCases?.map(e => e.note).join(" / "),
          proposalResponsible: i18n.t(`label.${c.proposalResponsible?.name ? 'responsible_assigned' : 'responsible_to_assign'}`),
          approvalResponsible: i18n.t(`label.${c.approvalResponsible?.name ? 'responsible_assigned' : 'responsible_to_assign'}`),
          verificationResponsible: i18n.t(`label.${c.verificationResponsible?.name ? 'responsible_assigned' : 'responsible_to_assign'}`),
          actionPlans: c.actionPlans?.map(e => ({
            ...e,
            executionResponsible: i18n.t(`label.${e.executionResponsible?.name ? 'responsible_assigned' : 'responsible_to_assign'}`),
            startDateConfig: {
              ...flatPickrConfig,
              maxDate: e.type === 'immediate' ? endOfToday : null,
            },
            endDateConfig: {
              ...flatPickrConfig,
              minDate: e.startDate || null,
            },
            lastModifiedDate: e.lastModifiedDate ? `${(new Date(e.lastModifiedDate)).toLocaleDateString(`${clientData.value.default_language || 'en'}-US`, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}hs` : null,
          })) || [],
          state: setState(c, startOfToday)
        }

        // Set reactive properties
        complaintData.value.actionPlans.forEach(e => {
          set(e, 'approvalNextStage', e.stage === 'analysis' || e.stage === 'corrective' || e.stage === 'verification' ? e.stage : '')
          set(e, 'correctiveNextStage', e.stage === 'analysis' || e.stage === 'verification' ? e.stage : '')
          set(e, 'verificationNextStage', e.stage === 'analysis' || e.stage === 'corrective' || e.stage === 'end' ? e.stage : '')
          set(e, 'implementationNextStage', e.stage === 'correctiveMeasures' || e.stage === 'finalized' ? e.stage : '')
          set(e, 'finalizedNextStage', e.stage === 'correctiveMeasures' || e.stage === 'implementation' || e.stage === 'end' ? e.stage : '')
          set(e, 'evidenceFile', [])
        })

        // Determine the type of user that is reviewing the complaint
        userType.value = c.authorUserNumber === userNumber.value ? 'author' : c.affectedUserNumber === userNumber.value ? 'affected' : 'reported'

        updateActiveTab.value = !updateActiveTab.value
        isSubmitted.value = true
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t(`message.${error?.message === 'invalidUserAndCaseNumbers' ? 'invalidUserAndCaseNumbers' : 'complaint_fetch_error'}`))
      } finally {
        isSubmitting.value = false
      }
    }

    const getName = (name) => {
      return name?.startsWith('other_') ? i18n.t(`label.${name}`) : name
    }

    const goToNewComplaint = () => {
      const routeData = router.resolve({ name: 'complaint-new', params: { clientPublicId } })
      window.open(routeData.href, '_blank')
    }

    const addNewMessage = (message) => {
      try {
        const query = { caseNumber: caseNumber.value }
        const action = { $push: { messages: message } }
        updateItem({ collection, query, action })
      } catch (error) {
        console.log(error)
      }
    }

    onMounted(async () => {
      try {
        // If route doesn't have the client public id, throw error
        if (!clientPublicId) throw new Error("Client public id not present in route params")

        // If user is not logged in (either as a real or anonymous user), then log in as an anonymous user
        // if (!useJwt.realm.currentUser) await useJwt.loginAsAnonymousUser()

        // Get client data based on client public id from URL param
        const item = await getItem({ collection: 'client', query: { publicId: Number(clientPublicId) } })
        if (!item) throw new Error('Client not found')
        
        item._id = item._id.toString()
        clientData.value = item

        // Define locale based on client default language
        i18n.locale = clientData.value.default_language
        localize(clientData.value.default_language)

      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.err_client_fetch'))
        clientNotFound.value = true

      } finally {
        isLoading.value = false
      }
    })

    return {
      userNumber,
      caseNumber,
      required,
      email,
      clientNotFound,
      isLoading,
      submitData,
      router,
      goToNewComplaint,
      isSubmitting,
      isSubmitted,
      complaintData,
      updateActiveTab,
      addNewMessage,
      clientData,
      userType,
      bgImg,
      logoVielco,
      isVielcoClient,
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/pages/page-auth.scss';
.custom-h2 {
  padding-left: 0.5rem !important;
  padding-bottom: 0;
  margin-bottom: 0;
  margin-right: 1rem;
}
.custom-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  overflow: hidden;
}

.img-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;   /* clave para cubrir el contenedor sin deformar */
  z-index: 0;
}

.card-width {
  max-width: 800px !important;
  margin: 0 auto;   /* centra la card */
}

@media (min-width: 768px) {
  .responsive-border {
    border-right: 1px solid #dee2e6;    /* borde a la derecha solo en pantallas grandes */
  }
}
</style>
